import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  InputBase,
  Paper,
  Portal,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { debounce } from 'lodash';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { ReactComponent as SearchIcon } from '../../../../../../../_base/layout/assets/layout-svg-icons/check.svg';
import { ReactComponent as CheckedIcon } from '../../../../../../../_base/layout/assets/layout-svg-icons/checked.svg';
import { ReactComponent as UncheckedIcon } from '../../../../../../../_base/layout/assets/layout-svg-icons/unchecked.svg';

import { useGetManufacturer } from '../../../hooks/useGetManufacturer';
import { Manufacturer } from '../../../../../../interfaces/manufacturer';
import { useManufacturerSearch } from '../../../hooks/useManufacturesSearch';
import { useFormatMessage } from '../../../../../../hooks';
import { IAppState } from '../../../../../../store/rootDuck';

export default function Manufacturers({
  categoryId,
  selected,
  setSelected,
  searchCatalog,
}: {
  categoryId?: number;
  selected: Manufacturer[];
  setSelected: React.Dispatch<React.SetStateAction<Manufacturer[]>>;
  searchCatalog?: string;
}) {
  const fm = useFormatMessage();
  const me = useSelector((store: IAppState) => store.profile.me);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const { setMnfc, clear, idsManufacturer } = useManufacturerSearch({
    initCheck: true,
    setSelected,
  });

  const {
    fetch,
    loadMore,
    manufacturers,
    loading,
    loadingMore,
    page,
    fetchSearch,
    loadMoreSearch,
  } = useGetManufacturer();

  const {
    fetch: fetchForAdmin,
    manufacturers: manufacturersForAdmin,
    clearManufacturers,
    loading: loadingForAdmin,
  } = useGetManufacturer();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredCities = useMemo(() => {
    if (me?.is_admin) {
      if (search && manufacturersForAdmin && manufacturersForAdmin.length > 0) {
        return manufacturersForAdmin || [];
      }
      if (search && manufacturersForAdmin && manufacturersForAdmin.length === 0) {
        return [];
      }
      return manufacturers || [];
    }
    return (
      manufacturers?.filter(city => city.name?.toLowerCase().includes(search.toLowerCase())) ||
      []
    );
  }, [search, manufacturers, me, manufacturersForAdmin]);

  const fetchDebouncedSearch = debounce(() => {
    if (search.trim() && search.length > 2 && me?.is_admin) {
      fetchForAdmin({
        page: 1,
        per_page: 50,
        text: me?.is_admin ? search : undefined,
      });
    }
  }, 1500);

  useEffect(() => {
    fetchDebouncedSearch();
    if (!search) {
      clearManufacturers();
    }
    return () => fetchDebouncedSearch.cancel();
  }, [search]);

  useEffect(() => {
    if (searchCatalog) {
      fetchSearch({
        search: searchCatalog,
        page: 1,
        // manufacturer_ids: idsManufacturer.join(','),
      });
    } else {
      fetch({
        category_id: categoryId || undefined,
        page: 1,
        per_page: 100,
      });
    }
  }, [categoryId, searchCatalog]);

  useEffect(() => {
    if (!loading && manufacturers && !loadingMore) {
      if (categoryId) {
        const newSelected = manufacturers.filter(item => idsManufacturer.includes(item.id));

        // if (newSelected.length > 0) {
        //   setMnfc(String(newSelected.map(item => item.id).join(',')));
        // } else {
        //   clear();
        // }

        setSelected(newSelected);
      } else {
        // const newSelected = manufacturers.filter(item => idsManufacturer.includes(item.id));
        // setSelected(newSelected);
      }
    }
  }, [loading, loadingMore]);

  const toggleCity = (mnfc: Manufacturer) => {
    setSelected(prev =>
      prev.find(item => item.id === mnfc.id)
        ? prev.filter(item => item.id !== mnfc.id)
        : [...prev, mnfc]
    );
  };

  const lengthSelected = useMemo(() => {
    const ids = [...idsManufacturer];

    selected.map(i => {
      if (!ids.includes(i.id)) {
        ids.push(i.id);
      }
    });

    return selected.length;
  }, [idsManufacturer, selected]);

  const handleScroll = async (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (search) return;

    const target = e.currentTarget;

    const isBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100;

    if (isBottom && !loadingMore && (manufacturers?.length || 0) < page.total) {
      if (searchCatalog) {
        await loadMoreSearch({
          search: searchCatalog,
          // manufacturer_ids: idsManufacturer.join(','),
        });
      } else {
        await loadMore({
          category_id: categoryId || undefined,
        });
      }
    }
  };

  return (
    <Box
      display='flex'
      flexDirection='row'
      gap='12px'
      sx={{
        '@media (max-width:768px)': {
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          '@media (max-width:768px)': {
            width: '100%',
          },
        }}
      >
        {/* TRIGGER */}
        <Box
          ref={anchorRef}
          onClick={() => setOpen(prev => !prev)}
          sx={{
            width: 256,
            height: 44,

            '@media (max-width:768px)': {
              width: '100%',
            },

            borderRadius: open ? '22px 22px 0 0' : '22px',

            backgroundColor: '#fff',

            // border: '1px solid #E3E3E6',

            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',

            paddingLeft: '12px',
            paddingRight: '6px',

            cursor: 'pointer',

            position: 'relative',
            zIndex: 1400,
          }}
        >
          <Box display='flex' alignItems='center'>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 400,
                color: '#181c32',
              }}
            >
              Производитель
            </Typography>
          </Box>

          <Box display='flex' gap='6px'>
            {/* COUNTER */}
            {lengthSelected > 0 && (
              <Box
                sx={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: '999px',
                  background: '#dddee1',

                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: '#181c32',
                  }}
                >
                  {lengthSelected}
                </Typography>
              </Box>
            )}

            {/* ARROW */}
            <IconButton
              size='small'
              sx={{
                width: 32,
                height: 32,

                background: '#E0E0E3',

                '&:hover': {
                  background: '#D8D8DC',
                },
              }}
            >
              {open ? (
                <KeyboardArrowUpIcon
                  sx={{
                    fontSize: 20,
                    color: '#37384A',
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 20,
                    color: '#37384A',
                  }}
                />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* DROPDOWN */}
        <Portal>
          {open && anchorRef.current && (
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Paper
                elevation={0}
                sx={{
                  position: 'absolute',

                  top: anchorRef.current.getBoundingClientRect().bottom + window.scrollY - 1,

                  left: anchorRef.current.getBoundingClientRect().left + window.scrollX,

                  width: anchorRef.current.offsetWidth,

                  borderRadius: '0 0 22px 22px',

                  background: '#fff',

                  // border: '1px solid #E3E3E6',
                  borderTop: 'none',

                  overflow: 'hidden',

                  zIndex: 1,

                  boxShadow: '0 20px 40px rgba(0,0,0,0.16)',
                }}
              >
                {/* SEARCH */}
                {categoryId || me?.is_admin ? (
                  <Box
                    sx={{
                      borderTop: '1px solid #DDDEE3',
                      borderBottom: '1px solid #DDDEE3',
                      height: '44px',
                      paddingLeft: '6px',
                      paddingRight: '13px',

                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Box>
                      <SearchIcon />
                    </Box>

                    <InputBase
                      placeholder='Поиск'
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      fullWidth
                      sx={{
                        fontSize: 17,
                        color: '#181c32',

                        '& input::placeholder': {
                          opacity: 1,
                          color: '#181c32',
                        },
                      }}
                    />
                    {loadingForAdmin && search.length > 2 && me?.is_admin && (
                      <Box>
                        <CircularProgress style={{ color: '#181c32' }} size={20} />
                      </Box>
                    )}
                  </Box>
                ) : null}

                {/* LIST */}
                <Box
                  ref={listRef}
                  onScroll={handleScroll}
                  sx={{
                    maxHeight: 560,
                    overflowY: 'auto',

                    display: 'flex',
                    flexDirection: 'column',

                    py: '7px',
                    gap: '13px',

                    '&::-webkit-scrollbar': {
                      width: 4,
                    },

                    '&::-webkit-scrollbar-thumb': {
                      background: '#dddee1',
                      borderRadius: 999,
                    },
                  }}
                >
                  {filteredCities.map(mnfc => {
                    const checked = selected.find(item => item.id === mnfc.id);

                    return (
                      <Box
                        key={mnfc.id}
                        onClick={() => toggleCity(mnfc)}
                        sx={{
                          paddingLeft: '6px',
                          paddingRight: '13px',

                          cursor: 'pointer',

                          '&:hover': {
                            background: '#ECECF1',
                          },
                        }}
                      >
                        <FormControlLabel
                          sx={{
                            m: 0,
                            width: '100%',

                            alignItems: 'center',

                            gap: '12px',

                            '& .MuiFormControlLabel-label': {
                              flex: 1,
                              minWidth: 0,
                            },

                            '& svg': {
                              flexShrink: 0,
                            },
                          }}
                          control={checked ? <CheckedIcon /> : <UncheckedIcon />}
                          label={
                            <Typography
                              sx={{
                                fontSize: 18,
                                color: '#37384A',

                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                lineHeight: 1.2,
                              }}
                            >
                              {mnfc.name}
                            </Typography>
                          }
                        />
                      </Box>
                    );
                  })}

                  {loadingMore && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: '#7E8299',
                        }}
                      >
                        {fm('COMMON.LOADING')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </ClickAwayListener>
          )}
        </Portal>
      </Box>
    </Box>
  );
}
